import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LinkErrorType } from 'shared/enums/link-error-type';
import { DeviceType } from 'shared/models/device-type';

import LinkableDevice from 'features/device/linkable-device/domain/models/linkable-device';
import { useDeviceLinkContextProvider } from 'features/device/providers/device-link-provider/device-link-provider';
import { ResidentDevice } from 'features/residents/models/resident-device';

type Props = {
  onClose: () => void;
  onLinkButtonClicked: (selectedDevices: ResidentDevice[]) => void;
};

type DeviceLinkModal = {
  selectedDevices: ResidentDevice[];
  hasDeviceLinkError: LinkErrorType | undefined;
  transmitterResidentSettings?: DeviceType[];
  isLinkableTransmitterStepsVisible: boolean;
  deviceIdToSelect: string | undefined;
  getErrorMessageForDevice: (device: LinkableDevice) => string | undefined;
  handleOnClose: () => void;
  handleOnLinkButtonClicked: () => void;
  handleOnResetFilters: () => void;
  toggleLinkableTransmitterSteps: () => void;
  handleTransmitterAdded: (createdDeviceId: string) => void;
};

const useDeviceLinkModal = ({ onClose, onLinkButtonClicked }: Props): DeviceLinkModal => {
  const [hasDeviceLinkError, setHasDeviceLinkError] = useState<LinkErrorType | undefined>(undefined);
  const [isLinkableTransmitterStepsVisible, setIsLinkableTransmitterStepsVisible] = useState<boolean>(false);
  const [deviceIdToSelect, setDeviceIdToSelect] = useState<string | undefined>(undefined);
  const { t } = useTranslation('residentsSettings', { keyPrefix: 'linkableDevicesModal' });
  const { selectedDevices, devicesToHide, transmitterResidentSettings, handleSelectedDevicesChanged } = useDeviceLinkContextProvider();

  const handleOnClose = (): void => {
    onClose();
    handleSelectedDevicesChanged([]);
    setIsLinkableTransmitterStepsVisible(false);
    setDeviceIdToSelect(undefined);
  };

  const toggleLinkableTransmitterSteps = (): void => {
    setIsLinkableTransmitterStepsVisible((prev) => !prev);
  };

  const handleTransmitterAdded = (createdDeviceId: string): void => {
    setIsLinkableTransmitterStepsVisible(false);
    setDeviceIdToSelect(createdDeviceId);
  };

  const handleOnLinkButtonClicked = (): void => {
    if (hasDeviceLinkError) {
      return;
    }
    onLinkButtonClicked(selectedDevices as ResidentDevice[]);
    handleSelectedDevicesChanged([]);
  };

  const handleOnResetFilters = (): void => {
    setHasDeviceLinkError(undefined);
    handleSelectedDevicesChanged([]);
  };

  const getErrorMessageForDevice = (device: LinkableDevice): string | undefined => {
    if (
      hasDeviceLinkError === LinkErrorType.DesoAlreadyLinked &&
      device.type === DeviceType.DesoResidentTransmitter &&
      selectedDevices.some((d) => d.id === device.id)
    ) {
      return t('desoResidentTransmitterAlreadyLinkedToOrganisationUnit');
    }

    return undefined;
  };

  const handleOnSelectedDevicesChanged = (devices: ResidentDevice[]): void => {
    const desoDevices = devices.filter((x) => x.type === DeviceType.DesoResidentTransmitter);

    if (desoDevices.length > 1 || (devicesToHide.some((x) => x.type === DeviceType.DesoResidentTransmitter) && desoDevices.length > 0)) {
      setHasDeviceLinkError(LinkErrorType.DesoAlreadyLinked);
    } else {
      setHasDeviceLinkError(undefined);
    }
  };

  useEffect(() => {
    handleOnSelectedDevicesChanged(selectedDevices as ResidentDevice[]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDevices]);

  return {
    selectedDevices: selectedDevices as ResidentDevice[],
    hasDeviceLinkError,
    transmitterResidentSettings,
    isLinkableTransmitterStepsVisible,
    deviceIdToSelect,
    getErrorMessageForDevice,
    handleOnClose,
    handleOnLinkButtonClicked,
    handleOnResetFilters,
    toggleLinkableTransmitterSteps,
    handleTransmitterAdded,
  };
};

export default useDeviceLinkModal;
